import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FileText, Image as ImageIcon, ChevronRight, FolderOpen } from "lucide-react";

/**
 * CUST-EXP-001A · Document wallet preview. Reuses the existing document
 * shape served by GET /projects/{id}/documents. Groups documents by
 * their existing `category` (Handover / Certificates / Site Survey /
 * Customer Upload / …) — never invents new categories. Deep-links to
 * /documents for open / upload / handover-pack actions.
 */
export default function DocumentsPreview({ docs = [], loading = false }) {
    const grouped = useMemo(() => {
        const m = new Map();
        for (const d of docs) {
            const c = d.category || "Uncategorised";
            if (!m.has(c)) m.set(c, 0);
            m.set(c, m.get(c) + 1);
        }
        return Array.from(m.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4);
    }, [docs]);

    const latest = docs.slice(0, 3);

    return (
        <div className="pe-card p-5 md:p-6" data-testid="documents-preview">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <span className="pe-icon-tile w-8 h-8"><FileText size={14} strokeWidth={1.75} /></span>
                    <div>
                        <div className="text-[11px] tracking-[0.14em] uppercase text-[#4B5565]">Document Wallet</div>
                        <div className="text-base text-[#131B2F] font-medium" data-testid="documents-preview-count">
                            {loading ? "Loading…" : `${docs.length} document${docs.length === 1 ? "" : "s"}`}
                        </div>
                    </div>
                </div>
                <Link
                    to="/documents"
                    data-testid="documents-preview-view"
                    className="text-sm text-[#3E9E2E] hover:text-[#2E7E22] font-medium inline-flex items-center gap-1"
                >
                    Open <ChevronRight size={14} />
                </Link>
            </div>

            {loading ? (
                <div className="text-sm text-[#4B5565]">Loading documents…</div>
            ) : docs.length === 0 ? (
                <div className="text-sm text-[#4B5565]" data-testid="documents-preview-empty">
                    Your installer will file certificates, warranties and the handover pack here.
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="documents-preview-categories">
                        {grouped.map(([cat, n]) => (
                            <span
                                key={cat}
                                className="pe-chip !bg-[#F1F8EC] !border-transparent !text-[#2E7E22] font-medium"
                                data-testid={`documents-preview-cat-${cat.toLowerCase().replace(/\s+/g,'-')}`}
                            >
                                <FolderOpen size={11} strokeWidth={1.75} /> {cat} · {n}
                            </span>
                        ))}
                    </div>
                    <ul className="space-y-1.5">
                        {latest.map((d) => {
                            const isImage = (d.file_type || "").startsWith("image/");
                            const Icon = isImage ? ImageIcon : FileText;
                            return (
                                <li
                                    key={d.id}
                                    data-testid={`documents-preview-item-${d.id}`}
                                    className="flex items-center gap-3 p-2.5 rounded-md hover:bg-[#F5F5F7] transition-colors"
                                >
                                    <Icon size={14} strokeWidth={1.75} className="text-[#4B5565] shrink-0" />
                                    <div className="text-sm text-[#131B2F] truncate flex-1">{d.title}</div>
                                    <div className="text-xs text-[#4B5565] shrink-0 hidden sm:block">{d.category}</div>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </div>
    );
}
